import React from 'react';

/**
 * FramedImage — renders an image inside a fixed-ratio frame WITHOUT ever
 * distorting or (by default) cropping the actual content.
 *
 * How it avoids the classic hero/banner problems:
 *   • The foreground image uses `object-contain`, so its own aspect ratio is
 *     always preserved — width and height are never scaled independently and
 *     the picture is never stretched.
 *   • When the source ratio differs from the frame ratio, the leftover space
 *     is filled with a blurred, zoomed copy of the SAME image (a decorative
 *     backdrop). Only this backdrop is cropped/`object-cover`-ed; the real
 *     content stays fully visible. Set `blurBackground={false}` for a plain
 *     neutral fill instead.
 *
 * This is the shared primitive for every fixed-ratio image slot (location
 * heroes, listing cards/galleries, ad creatives) so the no-distortion rule
 * lives in one place rather than as per-page CSS.
 *
 * Props:
 *   src            image URL (already absolute/served path).
 *   alt            alt text (backdrop copy is aria-hidden).
 *   ratio          Tailwind aspect class, e.g. 'aspect-[1336/420]'.
 *   blurBackground fill empty space with a blurred copy of the image.
 *   frameClassName extra classes on the outer frame.
 *   imgClassName   extra classes on the foreground <img>.
 *   testId         data-testid for the foreground image.
 *   loading        native <img> loading attr.
 */
export const FramedImage = ({
  src,
  alt = '',
  ratio = 'aspect-[16/9]',
  blurBackground = true,
  frameClassName = '',
  imgClassName = '',
  testId,
  loading,
}) => {
  if (!src) return null;
  return (
    <div className={`relative w-full overflow-hidden ${ratio} ${blurBackground ? 'bg-muted' : 'bg-white'} ${frameClassName}`}>
      {blurBackground && (
        <img
          src={src}
          alt=''
          aria-hidden='true'
          className='absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60 select-none pointer-events-none'
          loading={loading}
        />
      )}
      <img
        src={src}
        alt={alt}
        data-testid={testId}
        loading={loading}
        className={`relative z-10 w-full h-full object-contain ${imgClassName}`}
      />
    </div>
  );
};

export default FramedImage;
